"use client";

import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingCart, Search } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { CartDrawer } from "@/components/CartDrawer";
import { products } from "@/data/products";
import samboardsLogo from "@/assets/samboards-logo.png";

type SimpleLink = {
  label: string;
  to: string;
};

type NavItem = {
  label: string;
  to?: string;
  children?: SimpleLink[];
};

const navItems: NavItem[] = [
  {
    label: "Home",
    to: "/",
  },
  {
    label: "About",
    to: "/about",
  },
  {
    label: "Blog",
    to: "/blog",
  },
  {
    label: "Products",
    children: [
      { label: "Samboards", to: "/samboards" },
      { label: "Library Squares", to: "/library-squares" },
      { label: "Satpin", to: "/satpin" },
      { label: "Sensory aids to help with mobility and calming", to: "/sensory-aids" },
    ],
  },
  {
    label: "Solutions",
    children: [
      { label: "Care Homes", to: "/solutions/care-homes" },
      { label: "SEND Schools", to: "/solutions/send-schools" },
      { label: "Charities", to: "/solutions/charities" },
      {
        label: "Hospitals & Healthcare",
        to: "/solutions/hospitals-healthcare",
      },
      {
        label: "Local Authorities",
        to: "/solutions/local-authorities",
      },
      { label: "Corporate", to: "/solutions/corporate" },
      { label: "Parents & Carers", to: "/solutions/parents-carers" },
    ],
  },
  {
    label: "Access to Work Kits",
    to: "/access-to-work-kits",
  },
  {
    label: "Mental Health",
    children: [
      { label: "Anxiety & Stress", to: "/mental-health/anxiety-stress" },
      { label: "Autism", to: "/mental-health/autism" },
      { label: "Dementia", to: "/mental-health/dementia" },
      { label: "Strokes", to: "/mental-health/strokes" },
    ],
  },
  {
    label: "Feedback",
    to: "/feedback",
    children: [
      { label: "Feedback", to: "/feedback" },
      { label: "Research & Studies", to: "/research-studies" },
    ],
  },
  {
    label: "Shop",
    to: "/shop",
  },
];

const linkBaseClasses =
  "text-sm font-medium text-slate-100/80 hover:text-cyan-300 transition-colors";

const activeClasses = "text-cyan-300";

const Navigation: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { totalItems } = useCart();
  const closeTimeout = useRef<number | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  
  // Check if we're in preview environment (not published site)
  const isPreview = typeof window !== 'undefined' && 
    (window.location.hostname.includes('lovable.app') || 
     window.location.hostname === 'localhost' ||
     window.location.hostname === '127.0.0.1');

  const handleOpen = (label: string) => {
    if (closeTimeout.current) {
      window.clearTimeout(closeTimeout.current);
    }
    setOpenItem(label);
  };

  const handleClose = () => {
    // Small delay so the user can move from the label into the panel
    closeTimeout.current = window.setTimeout(() => {
      setOpenItem(null);
    }, 150);
  };

  // Search functionality
  const allPages = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Shop", to: "/shop" },
    { label: "Feedback", to: "/feedback" },
    { label: "FAQ", to: "/faq" },
    { label: "Contact", to: "/contact" },
    ...navItems.flatMap(item => 
      item.children ? item.children : item.to ? [{ label: item.label, to: item.to }] : []
    )
  ];

  const searchResults = searchQuery.trim() 
    ? [
        ...products.filter(p => 
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 5),
        ...allPages.filter(p => 
          p.label.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 3)
      ]
    : [];

  const handleSearchClick = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
  };

  const handleResultClick = (path: string) => {
    navigate(path);
    closeSearch();
  };

  // Close search on ESC or click outside
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && searchOpen) {
        closeSearch();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        closeSearch();
      }
    };

    if (searchOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen]);

  const renderDesktopItem = (item: NavItem) => {
    if (item.children && item.children.length > 0) {
      return (
        <div
          key={item.label}
          className="relative h-full flex items-center"
          onMouseEnter={() => handleOpen(item.label)}
          onMouseLeave={handleClose}
        >
          {/* Clickable parent link if item has 'to' property */}
          {item.to ? (
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                [
                  "inline-flex items-center gap-1 h-full",
                  linkBaseClasses,
                  isActive ? activeClasses : "",
                ].join(" ")
              }
            >
              <span>{item.label}</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </NavLink>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-1 h-full"
            >
              <span className={linkBaseClasses}>{item.label}</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>
          )}

          {openItem === item.label && (
            <div className="absolute left-0 top-full mt-3">
              <div className="rounded-2xl bg-[#050818]/95 border border-white/8 shadow-[0_24px_80px_rgba(0,0,0,0.7)] backdrop-blur-xl min-w-[220px] py-3">
                <ul className="flex flex-col">
                  {item.children.map((child) => (
                    <li key={child.to}>
                      <NavLink
                        to={child.to}
                        className={({ isActive }) =>
                          [
                            "block px-4 py-2.5 text-sm transition-colors",
                            isActive ? "text-cyan-300" : "text-slate-100/80",
                            "hover:text-cyan-300 hover:bg-white/5",
                          ].join(" ")
                        }
                      >
                        {child.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Simple (non-dropdown) link
    return (
      <NavLink
        key={item.label}
        to={item.to || "/"}
        className={({ isActive }) =>
          [
            linkBaseClasses,
            isActive ? activeClasses : "",
            "h-full inline-flex items-center",
          ].join(" ")
        }
      >
        {item.label}
      </NavLink>
    );
  };

  const renderMobileLinks = () => (
    <div className="mt-4 space-y-4 border-t border-white/10 pt-4">
      {navItems.map((item) => {
        if (item.children && item.children.length > 0) {
          return (
            <div key={item.label} className="space-y-2">
              {/* Parent link if it exists */}
              {item.to ? (
                <NavLink
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    [
                      "text-xs uppercase tracking-wide font-semibold",
                      isActive ? "text-cyan-300" : "text-slate-400",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ) : (
                <div className="text-xs uppercase tracking-wide text-slate-400">
                  {item.label}
                </div>
              )}
              <div className="space-y-1">
                {item.children.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      [
                        "block text-sm",
                        isActive ? "text-cyan-300" : "text-slate-100/80",
                      ].join(" ")
                    }
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        }

        return (
          <NavLink
            key={item.label}
            to={item.to || "/"}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              [
                "block text-sm",
                isActive ? "text-cyan-300" : "text-slate-100/80",
              ].join(" ")
            }
          >
            {item.label}
          </NavLink>
        );
      })}
      
      {/* Admin link - only in preview */}
      {isPreview && (
        <div className="pt-4 border-t border-amber-500/20">
          <NavLink
            to="/owner-dashboard"
            onClick={() => setMobileOpen(false)}
            className="block text-sm text-amber-300 font-semibold"
          >
            🔒 Owner Dashboard
          </NavLink>
        </div>
      )}
    </div>
  );

  return (
    <header className="fixed top-9 left-1/2 z-50 -translate-x-1/2 w-[95%] sm:w-[92%] md:w-[80%] lg:w-[70%]">
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="
          flex items-center justify-between
          px-4 py-3 md:px-6 md:py-4
          rounded-full
          bg-gradient-to-r from-[#050816] via-[#020617] to-[#050816]
          shadow-[0_24px_80px_rgba(0,0,0,0.75)]
          backdrop-blur-2xl
        "
      >
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0 mr-8 lg:mr-10">
          <img 
            src={samboardsLogo} 
            alt="Samboards" 
            width="591"
            height="344"
            className="h-8 w-auto md:h-10"
          />
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6 lg:gap-7 h-full flex-1 justify-center">
          {navItems.map(renderDesktopItem)}
        </div>

        {/* Search, Cart & Shop Now CTA – desktop */}
        <div className="hidden md:flex items-center gap-4 shrink-0">
          {/* Search */}
          <div ref={searchRef} className="relative">
            {!searchOpen ? (
              <button
                onClick={handleSearchClick}
                className="rounded-full p-2.5 text-slate-300 transition-colors hover:bg-white/5 hover:text-emerald-300"
                aria-label="Open search"
              >
                <Search className="h-5 w-5" />
              </button>
            ) : (
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products or pages..."
                  className="w-64 px-4 py-2 pr-10 rounded-full bg-slate-900/70 border border-slate-700/60 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-emerald-300/50 focus:ring-2 focus:ring-emerald-300/20 transition-all"
                />
                <button
                  onClick={closeSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-100 transition-colors"
                  aria-label="Close search"
                >
                  <X className="h-4 w-4" />
                </button>
                
                {/* Search Results Dropdown */}
                {searchQuery && searchResults.length > 0 && (
                  <div className="absolute top-full mt-2 w-full max-w-md rounded-2xl bg-[#050818]/98 border border-white/8 shadow-[0_24px_80px_rgba(0,0,0,0.8)] backdrop-blur-xl py-2 max-h-96 overflow-y-auto z-[100]">
                    {products.filter(p => 
                      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.description.toLowerCase().includes(searchQuery.toLowerCase())
                    ).slice(0, 5).length > 0 && (
                      <div className="px-3 py-2">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Products</p>
                        {products.filter(p => 
                          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.description.toLowerCase().includes(searchQuery.toLowerCase())
                        ).slice(0, 5).map(product => (
                          <button
                            key={product.slug}
                            onClick={() => handleResultClick(product.slug)}
                            className="w-full text-left px-3 py-2.5 rounded-xl text-slate-200/90 hover:text-emerald-300 hover:bg-white/5 transition-colors"
                          >
                            <div className="font-medium">{product.name}</div>
                            <div className="text-xs text-slate-400 mt-0.5 truncate">{product.description}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {allPages.filter(p => 
                      p.label.toLowerCase().includes(searchQuery.toLowerCase())
                    ).slice(0, 3).length > 0 && (
                      <div className="px-3 py-2 border-t border-white/5">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Pages</p>
                        {allPages.filter(p => 
                          p.label.toLowerCase().includes(searchQuery.toLowerCase())
                        ).slice(0, 3).map(page => (
                          <button
                            key={page.to}
                            onClick={() => handleResultClick(page.to)}
                            className="w-full text-left px-3 py-2.5 rounded-xl text-slate-200/90 hover:text-emerald-300 hover:bg-white/5 transition-colors"
                          >
                            {page.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {searchQuery && searchResults.length === 0 && (
                  <div className="absolute top-full mt-2 w-full rounded-2xl bg-[#050818]/98 border border-white/8 shadow-[0_24px_80px_rgba(0,0,0,0.8)] backdrop-blur-xl py-6 text-center z-[100]">
                    <p className="text-slate-400 text-sm">No results found for "{searchQuery}"</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setCartOpen(true)}
            className="relative rounded-full p-2.5 text-slate-300 transition-colors hover:bg-white/5 hover:text-emerald-300"
            aria-label="View shopping cart"
          >
            <ShoppingCart className="h-5 w-5" />
            {totalItems > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-[10px] font-bold text-slate-900">
                {totalItems}
              </span>
            )}
          </button>
          
          {isPreview && (
            <Link
              to="/owner-dashboard"
              className="
                relative inline-flex items-center justify-center
                px-4 py-2 rounded-full text-xs font-semibold
                text-amber-300
                bg-amber-900/30
                border border-amber-500/30
                hover:bg-amber-900/50 hover:border-amber-500/50
                transition-all
              "
              title="Owner Dashboard (Preview Only)"
            >
              Admin
            </Link>
          )}
          
          <Link
            to="/shop"
            className="
              relative inline-flex items-center justify-center
              px-5 py-2.5 rounded-full text-sm font-semibold
              text-black
              bg-gradient-to-r from-[#9ff3e3] to-[#8ad0ff]
              shadow-[0_0_40px_rgba(159,243,227,0.45)]
              hover:shadow-[0_0_55px_rgba(159,243,227,0.7)]
              transition-transform transition-shadow
              hover:-translate-y-0.5
            "
          >
            Shop now
          </Link>
        </div>

        {/* Mobile cart & menu button */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={handleSearchClick}
            className="rounded-full p-2.5 text-slate-300 transition-colors hover:bg-white/5"
            aria-label="Open search"
          >
            <Search className="h-5 w-5" />
          </button>
          
          <button
            onClick={() => setCartOpen(true)}
            className="relative rounded-full p-2.5 text-slate-300 transition-colors hover:bg-white/5"
            aria-label="View shopping cart"
          >
            <ShoppingCart className="h-5 w-5" />
            {totalItems > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-[10px] font-bold text-slate-900">
                {totalItems}
              </span>
            )}
          </button>
          
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-2.5 text-slate-100 hover:bg-white/10 transition"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="mt-3 md:hidden rounded-2xl bg-[#050818]/95 border border-white/8 shadow-[0_24px_80px_rgba(0,0,0,0.8)] backdrop-blur-2xl px-5 py-4">
          {renderMobileLinks()}
          <div className="mt-4">
            <Link
              to="/shop"
              onClick={() => setMobileOpen(false)}
              className="
                inline-flex items-center justify-center
                w-full px-4 py-2.5 rounded-full text-sm font-semibold
                text-black
                bg-gradient-to-r from-[#9ff3e3] to-[#8ad0ff]
                shadow-[0_0_30px_rgba(159,243,227,0.45)]
              "
            >
              Shop now
            </Link>
          </div>
        </div>
      )}

      {/* Mobile Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] md:hidden bg-slate-950/95 backdrop-blur-xl" onClick={closeSearch}>
          <div className="mt-20 px-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products or pages..."
                className="w-full px-4 py-3 pr-12 rounded-2xl bg-slate-900/70 border border-slate-700/60 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-emerald-300/50 focus:ring-2 focus:ring-emerald-300/20 transition-all text-base"
              />
              <button
                onClick={closeSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-100 transition-colors"
                aria-label="Close search"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Mobile Search Results */}
            {searchQuery && searchResults.length > 0 && (
              <div className="mt-4 rounded-2xl bg-[#050818]/98 border border-white/8 shadow-[0_24px_80px_rgba(0,0,0,0.8)] backdrop-blur-xl py-3 max-h-[60vh] overflow-y-auto">
                {products.filter(p => 
                  p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  p.description.toLowerCase().includes(searchQuery.toLowerCase())
                ).slice(0, 5).length > 0 && (
                  <div className="px-4 py-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Products</p>
                    {products.filter(p => 
                      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.description.toLowerCase().includes(searchQuery.toLowerCase())
                    ).slice(0, 5).map(product => (
                      <button
                        key={product.slug}
                        onClick={() => handleResultClick(product.slug)}
                        className="w-full text-left px-4 py-3 rounded-xl text-slate-200/90 hover:text-emerald-300 hover:bg-white/5 transition-colors mb-1"
                      >
                        <div className="font-medium">{product.name}</div>
                        <div className="text-xs text-slate-400 mt-1 line-clamp-2">{product.description}</div>
                      </button>
                    ))}
                  </div>
                )}
                
                {allPages.filter(p => 
                  p.label.toLowerCase().includes(searchQuery.toLowerCase())
                ).slice(0, 3).length > 0 && (
                  <div className="px-4 py-2 border-t border-white/5 mt-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Pages</p>
                    {allPages.filter(p => 
                      p.label.toLowerCase().includes(searchQuery.toLowerCase())
                    ).slice(0, 3).map(page => (
                      <button
                        key={page.to}
                        onClick={() => handleResultClick(page.to)}
                        className="w-full text-left px-4 py-3 rounded-xl text-slate-200/90 hover:text-emerald-300 hover:bg-white/5 transition-colors mb-1"
                      >
                        {page.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {searchQuery && searchResults.length === 0 && (
              <div className="mt-4 rounded-2xl bg-[#050818]/98 border border-white/8 shadow-[0_24px_80px_rgba(0,0,0,0.8)] backdrop-blur-xl py-8 text-center">
                <p className="text-slate-400 text-sm">No results found for "{searchQuery}"</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </header>
  );
};

export default Navigation;